import type { EmailProviderType } from "@app/lib/utils/email_provider_detection";
import type { FavoritePlatform } from "@app/types/favorite_platforms";
import { FAVORITE_PLATFORM_OPTIONS } from "@app/types/favorite_platforms";
import type { JobType } from "@app/types/job_type";
import { JOB_TYPE_OPTIONS } from "@app/types/job_type";
import { asDisplayName } from "@app/types/shared/utils/string_utils";
import type { WorkspaceType } from "@app/types/user";
import {
  Button,
  Card,
  Chip,
  ConfluenceLogo,
  DustLogoSquare,
  FrontLogo,
  GithubLogo,
  GmailLogo,
  HubspotLogo,
  Icon,
  Input,
  JiraLogo,
  MicrosoftOutlookLogo,
  NotionLogo,
  Page,
  SlackLogo,
} from "@dust-tt/sparkle";
import i18n from "i18next";
import type { ComponentType } from "react";
import { Trans, useTranslation } from "react-i18next";

const PLATFORM_ICONS: Record<FavoritePlatform, ComponentType> = {
  gmail: GmailLogo,
  outlook: MicrosoftOutlookLogo,
  slack: SlackLogo,
  notion: NotionLogo,
  confluence: ConfluenceLogo,
  github: GithubLogo,
  hubspot: HubspotLogo,
  jira: JiraLogo,
  front: FrontLogo,
};

export interface ProfileFormData {
  firstName: string;
  lastName: string;
  jobType: JobType | null;
}

export interface ProfileFormErrors {
  firstName?: string;
  lastName?: string;
  jobType?: string;
}

export function validateProfileForm(data: ProfileFormData): ProfileFormErrors {
  const errors: ProfileFormErrors = {};
  if (!data.firstName.trim()) {
    errors.firstName = i18n.t(
      "pages.onboardingFirstNameRequired",
      "First name is required"
    );
  }
  if (!data.lastName.trim()) {
    errors.lastName = i18n.t(
      "pages.onboardingLastNameRequired",
      "Last name is required"
    );
  }
  if (!data.jobType) {
    errors.jobType = i18n.t(
      "pages.onboardingJobTypeRequired",
      "Please select your job type"
    );
  }
  return errors;
}

export function getInitialSelectedPlatforms(
  emailProvider: EmailProviderType
): Set<FavoritePlatform> {
  const platforms = new Set<FavoritePlatform>();
  if (emailProvider === "google") {
    platforms.add("gmail");
  } else if (emailProvider === "microsoft") {
    platforms.add("outlook");
  }
  return platforms;
}

interface UserProfileStepProps {
  owner: WorkspaceType;
  isAdmin: boolean;
  formData: ProfileFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProfileFormData>>;
  formErrors: ProfileFormErrors;
  showErrors: boolean;
  onNext: () => void;
}

export function UserProfileStep({
  owner,
  isAdmin,
  formData,
  setFormData,
  formErrors,
  showErrors,
  onNext,
}: UserProfileStepProps) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col gap-8 pt-4 md:justify-center md:pt-0">
      <Page.Header
        title={t("pages.onboardingHelloName", "Hello {{name}}!", {
          name: formData.firstName || "there",
        })}
        icon={() => <DustLogoSquare className="-ml-11 h-10 w-32" />}
      />
      <p className="text-muted-foreground">
        {t("pages.onboardingCheckFewThings", "Let's check a few things.")}
      </p>
      {!isAdmin && (
        <p className="text-muted-foreground">
          <Trans
            i18nKey="pages.onboardingJoiningWorkspaceFull"
            defaults="You'll be joining the workspace: <0>{{workspaceName}}</0>."
            values={{ workspaceName: owner.name }}
            components={[<span key="0" className="font-medium" />]}
          />
        </p>
      )}
      <div>
        <p className="pb-2 text-muted-foreground">
          {t("pages.onboardingYourNameIs", "Your name is:")}
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Input
              name="firstName"
              placeholder={t(
                "pages.onboardingFirstNamePlaceholder",
                "First Name"
              )}
              value={formData.firstName}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))
              }
            />
            {showErrors && formErrors.firstName && (
              <p className="mt-1 text-sm text-red-500">
                {formErrors.firstName}
              </p>
            )}
          </div>
          <div>
            <Input
              name="lastName"
              placeholder={t(
                "pages.onboardingLastNamePlaceholder",
                "Last Name"
              )}
              value={formData.lastName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
              }
            />
            {showErrors && formErrors.lastName && (
              <p className="mt-1 text-sm text-red-500">{formErrors.lastName}</p>
            )}
          </div>
        </div>
      </div>
      <div>
        <p className="pb-2 text-muted-foreground">
          {t(
            "pages.onboardingPickRole",
            "Pick your role to customize your experience:"
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {JOB_TYPE_OPTIONS.map((jobTypeOption) => (
            <Chip
              key={jobTypeOption.value}
              // JOB_TYPE_LABELS itself stays English: it is also fed into LLM
              // prompts (sidekick_context, activation skill, user_analytics).
              // Translate at the display boundary only.
              label={t(
                `pages.onboardingJobType.${jobTypeOption.value}`,
                jobTypeOption.label
              )}
              size="xs"
              color={
                formData.jobType === jobTypeOption.value
                  ? "highlight"
                  : "primary"
              }
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  jobType: jobTypeOption.value,
                }))
              }
            />
          ))}
        </div>
        {showErrors && formErrors.jobType && (
          <p className="mt-1 text-sm text-red-500">{formErrors.jobType}</p>
        )}
      </div>
      <div className="flex justify-end">
        <Button
          label={t("pages.nextButton", "Next")}
          size="md"
          onClick={onNext}
        />
      </div>
    </div>
  );
}

interface FavoritePlatformsStepProps {
  selectedPlatforms: Set<FavoritePlatform>;
  onTogglePlatform: (platform: FavoritePlatform) => void;
  onSubmit: React.MouseEventHandler<HTMLElement>;
  isSubmitting: boolean;
}

export function FavoritePlatformsStep({
  selectedPlatforms,
  onTogglePlatform,
  onSubmit,
  isSubmitting,
}: FavoritePlatformsStepProps) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col gap-8 pt-4 md:justify-center md:pt-0">
      <Page.Header
        title={t(
          "pages.onboardingFavoritePlatformsTitle",
          "What are your favorite platforms?"
        )}
      />
      <p className="text-muted-foreground">
        {t(
          "pages.onboardingPlatformsDesc",
          "Dasis works at full potential when it can play with your knowledge and help with your tools. Do you recognise some of these?"
        )}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {FAVORITE_PLATFORM_OPTIONS.map((platform) => {
          const PlatformIcon = PLATFORM_ICONS[platform];
          const isSelected = selectedPlatforms.has(platform);
          return (
            <Card
              key={platform}
              variant="secondary"
              size="sm"
              selected={isSelected}
              onClick={() => onTogglePlatform(platform)}
            >
              <div className="flex items-center gap-3">
                <Icon visual={PlatformIcon} size="md" />
                <span className="text-sm font-medium">
                  {asDisplayName(platform)}
                </span>
              </div>
            </Card>
          );
        })}
      </div>
      <div className="flex justify-end">
        <Button
          label={t("pages.nextButton", "Next")}
          isLoading={isSubmitting}
          disabled={isSubmitting}
          size="md"
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
